import {v4 as uuidv4} from 'uuid'

import Currency from '@/types/currency'
import compactObject from '@/utils/compact-object'
import locales, {CountryCode as SupportedCountryCode} from '@helios/shared/locale/locales'
import {track, identify, TrackingEvent} from '@helios/shared/utils/analytics'

import {State as BigformFormState} from './bigform'
import {State as DemoFormState} from './demo-form'
import {State as EventBigformFormState} from './event/event-bigform'
import {State as HubspotGlobalFormState} from './global/hubspot-global-form'
import {State as PartnerBigformFormState} from './partner/partner-bigform'
import {State as HubspotReferralFormState} from './referral/hubspot-referral-form'

export const OTHER = 'Other' as const

const isValidCountry = (country?: string): country is SupportedCountryCode =>
    !!country && locales.some((locale) => locale.country === country)

export enum FunnelStage {
    BigformReached = 'bigform_reached',
    BigformSubmitted = 'bigform_submitted',
    PleoDemoReached = 'pleodemo_reached',
    PleoDemoBooked = 'pleodemo_booked',
}

export const getConversionValue = (country: string) => (funnelStage: FunnelStage) => {
    if (!country || !isValidCountry(country)) {
        return null
    }

    type CountryMap = Record<SupportedCountryCode, number>
    type ValueMap = Record<FunnelStage, CountryMap>

    const valueMap: ValueMap = {
        [FunnelStage.BigformReached]: {
            dk: 86,
            se: 74,
            de: 72,
            gb: 80,
            ie: 80,
            es: 64,
            fi: 1,
            at: 1,
            fr: 1,
            nl: 1,
        },
        [FunnelStage.BigformSubmitted]: {
            dk: 146,
            se: 135,
            de: 116,
            gb: 122,
            ie: 122,
            es: 98,
            fi: 1,
            at: 1,
            fr: 1,
            nl: 1,
        },
        [FunnelStage.PleoDemoReached]: {
            dk: 877,
            se: 867,
            de: 468,
            gb: 463,
            ie: 463,
            es: 241,
            fi: 1,
            at: 1,
            fr: 1,
            nl: 1,
        },
        [FunnelStage.PleoDemoBooked]: {
            dk: 1998,
            se: 2279,
            de: 781,
            gb: 741,
            ie: 741,
            es: 526,
            fi: 1,
            at: 1,
            fr: 1,
            nl: 1,
        },
    }

    return valueMap[funnelStage][country]
}

interface BigformReachedArgs {
    country: SupportedCountryCode
    trackQualifyingLeads?: boolean
}

export const bigformReached = (data: BigformReachedArgs) => {
    const params = new URLSearchParams(window.location?.search)
    const email = params.get('email')

    const {country, trackQualifyingLeads} = data

    email && identify({email})
    track(TrackingEvent.BigformActioned, {action: 'started'})
    track(TrackingEvent.BigformViewed)

    const conversionValue = getConversionValue(country)(FunnelStage.BigformReached)

    if (trackQualifyingLeads) {
        track(TrackingEvent.SignupActioned, {action: 'started'})
        /** We trigger OrderCompleted event, even without accurate/complete values.
         * It is related to the iOS14/ATT changes,
         * which impacts event optimization on Facebook ads and Google ads events */
        track(TrackingEvent.OrderCompleted, {
            total: conversionValue || 0,
            revenue: conversionValue || 0,
            currency: Currency.EUR,
            funnel_stage: FunnelStage.BigformReached,
            company_employee_size: '',
            country_code: country || '',
        })
    }
}

type BigformSubmittedArgs =
    | BigformFormState
    | DemoFormState
    | HubspotGlobalFormState
    | PartnerBigformFormState
    | HubspotReferralFormState
    | EventBigformFormState

export const bigformSubmitted = (data: BigformSubmittedArgs) => {
    const {email, country} = data
    const traits = compactObject({
        email,
        phone: 'phoneNumber' in data && data.phoneNumber,
        name: 'userFullName' in data && data.userFullName,
        firstName: 'firstName' in data && data.firstName,
        lastName: 'lastName' in data && data.lastName,
        marketingEmails: 'marketingEmails' in data && data.marketingEmails,
        form: 'DirectBigForm',
        external_id: window.location.pathname.includes('/demo') && uuidv4(),
        company: {
            name: 'companyName' in data && data.companyName,
            employee_count:
                'companySize' in data &&
                Number(data.companySize.split('-').pop()?.replace('+', '')),
        },
    })

    identify(traits)
    track(TrackingEvent.BigformActioned, {action: 'completed', ...data})
    track(TrackingEvent.BigformSubmitted, data)

    if (!country) {
        return
    }

    const conversionValue = getConversionValue(country)(FunnelStage.BigformSubmitted)

    if (!conversionValue) {
        return
    }
}

// Track interaction with fields (e.g. typed character in text input, or selection in select)
// We only track interaction with each element once per session
const fieldsTracked: Record<string, boolean> = {}
export const bigformInteracted = (field: string) => {
    if (fieldsTracked[field]) {
        return
    }
    track(TrackingEvent.BigformActioned, {action: 'interacted', field})
    fieldsTracked[field] = true
}

interface PleoDemoReachedArgs {
    country?: string | null
    companySize?: string | null
}

export const pleoDemoReached = (data: PleoDemoReachedArgs) => {
    const {country} = data

    track(TrackingEvent.DemoBookingActioned, {action: 'started'})
    track(TrackingEvent.DemoBookingViewed)

    if (!country) {
        return
    }

    const conversionValue = getConversionValue(country)(FunnelStage.PleoDemoReached)

    if (!conversionValue) {
        return
    }
}

interface PleoDemoBookedArgs {
    country?: string | null
    companySize?: string | null
}

export const pleoDemoBooked = (data: PleoDemoBookedArgs) => {
    const {country} = data

    track(TrackingEvent.DemoBookingActioned, {action: 'completed'})
    track(TrackingEvent.DemoBookingSubmitted)

    if (!country) {
        return
    }

    const conversionValue = getConversionValue(country)(FunnelStage.PleoDemoBooked)

    if (!conversionValue) {
        return
    }
}

export const demoBookingTimeSelected = () => {
    track(TrackingEvent.DemoBookingActioned, {action: 'clicked'})
}
